/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================
*/

import { useEffect, useMemo, useState } from "react";
import { useLocation, NavLink } from "react-router-dom";
import PropTypes from "prop-types";
import { useAuth } from "pages/context/AuthContext";

import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Icon from "@mui/material/Icon";
import Collapse from "@mui/material/Collapse";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

import SidenavCollapse from "examples/Sidenav/SidenavCollapse";
import SidenavRoot from "examples/Sidenav/SidenavRoot";
import sidenavLogoLabel from "examples/Sidenav/styles/sidenavStyle";

import {
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
} from "context";

function Sidenav({ color, brand, brandName, routes, ...rest }) {
  const { temPermissao } = useAuth();

  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    transparentSidenav,
    whiteSidenav,
    darkMode,
    uiOverrides,
    getGradientFromColor,
  } = controller;

  const location = useLocation();
  const collapseName = location.pathname.replace("/", "").replaceAll("/", "-");
  const [openCollapse, setOpenCollapse] = useState({});

  const toKey = (s) =>
    (s || "")
      .normalize("NFKC")
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9\-]/g, "");

  const toggleCollapse = (key) => {
    setOpenCollapse((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleMouseEnter = () => {
    if (miniSidenav) setMiniSidenav(dispatch, false);
  };
  const handleMouseLeave = () => {
    if (!miniSidenav) setMiniSidenav(dispatch, true);
  };

  let textColor = "white";
  if (transparentSidenav || (whiteSidenav && !darkMode)) textColor = "dark";
  else if (whiteSidenav && darkMode) textColor = "inherit";

  const closeSidenav = () => setMiniSidenav(dispatch, true);

  useEffect(() => {
    function handleMiniSidenav() {
      setMiniSidenav(dispatch, window.innerWidth < 1200);
      setTransparentSidenav(dispatch, window.innerWidth < 1200 ? false : transparentSidenav);
      setWhiteSidenav(dispatch, window.innerWidth < 1200 ? false : whiteSidenav);
    }
    window.addEventListener("resize", handleMiniSidenav);
    handleMiniSidenav();
    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatch, location]);

  // Normalização defensiva para comparação de acessos
  const norm = (s) => (s || "").normalize("NFKC").trim().toLowerCase();

  // true se o utilizador tiver leitura no(s) acesso(s)
  function hasRead(acesso) {
    if (!acesso) return false;
    if (Array.isArray(acesso)) return acesso.some((a) => temPermissao(a, "r"));
    return temPermissao(acesso, "r");
  }

  // Herda 'acesso' do pai para os filhos que não o tiverem definido
  function attachParentAccess(nodes, parentAccess = null) {
    return nodes.map((n) => {
      const currentAccess = n.acesso ?? parentAccess;
      const children = Array.isArray(n.collapse) ? n.collapse : null;
      const withKey = { ...n, key: n.key || toKey(n.name) };
      if (children && children.length) {
        return {
          ...withKey,
          acesso: withKey.acesso ?? currentAccess,
          collapse: attachParentAccess(children, currentAccess),
        };
      }
      return { ...withKey, acesso: withKey.acesso ?? currentAccess };
    });
  }

  // Visibilidade: pai só aparece se tiver pelo menos 1 filho visível; folha precisa de R
  function isVisible(route) {
    if (route?.public) return true;
    const children = Array.isArray(route?.collapse) ? route.collapse : null;
    if (children && children.length) return children.some((c) => isVisible(c));
    return hasRead(route?.acesso);
  }

  // Aplica herança de acesso e limpa a árvore
  const filteredRoutes = useMemo(() => {
    const withInherited = attachParentAccess(routes);
    const cleaned = withInherited
      .map((r) => {
        const children = Array.isArray(r.collapse) ? r.collapse : null;
        if (children && children.length) {
          const visibleKids = children.filter((kid) => isVisible(kid));
          return visibleKids.length ? { ...r, collapse: visibleKids } : null;
        }
        return isVisible(r) ? r : null;
      })
      .filter(Boolean);
    return cleaned;
  }, [routes]);

  const renderRoutes = filteredRoutes.map(
    ({ type, name, icon, title, noCollapse, key, href, route, collapse: children }) => {
      if (type === "collapse" && Array.isArray(children)) {
        const k = key || toKey(name);
        return (
          <div key={k}>
            <SidenavCollapse
              name={name}
              icon={icon}
              active={false}
              onClick={() => toggleCollapse(k)}
              noCollapse={false}
              open={!!openCollapse[k]}
            />
            <Collapse in={!!openCollapse[k]} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {children.map((child) =>
                  child.href ? (
                    <Link
                      href={child.href}
                      key={child.key || toKey(child.name)}
                      target="_blank"
                      rel="noreferrer"
                      sx={{ textDecoration: "none" }}
                    >
                      <SidenavCollapse
                        name={child.name}
                        icon={child.icon}
                        active={child.route === location.pathname}
                        nested
                      />
                    </Link>
                  ) : (
                    <NavLink key={child.key || toKey(child.name)} to={child.route}>
                      <SidenavCollapse
                        name={child.name}
                        icon={child.icon}
                        active={child.route === location.pathname}
                        nested
                      />
                    </NavLink>
                  )
                )}
              </List>
            </Collapse>
          </div>
        );
      }

      if (type === "collapse") {
        const k = key || toKey(name);
        return href ? (
          <Link
            href={href}
            key={k}
            target="_blank"
            rel="noreferrer"
            sx={{ textDecoration: "none" }}
          >
            <SidenavCollapse
              name={name}
              icon={icon}
              active={k === collapseName}
              noCollapse={noCollapse}
            />
          </Link>
        ) : (
          <NavLink key={k} to={route}>
            <SidenavCollapse name={name} icon={icon} active={k === collapseName} />
          </NavLink>
        );
      }

      if (type === "title") {
        return (
          <MDTypography
            key={key || toKey(title)}
            color={textColor}
            display="block"
            variant="caption"
            fontWeight="bold"
            textTransform="uppercase"
            pl={3}
            mt={2}
            mb={1}
            ml={1}
          >
            {title}
          </MDTypography>
        );
      }

      if (type === "divider") {
        return (
          <Divider
            key={key || toKey(name)}
            light={
              (!darkMode && !whiteSidenav && !transparentSidenav) ||
              (darkMode && !transparentSidenav && whiteSidenav)
            }
          />
        );
      }

      return null;
    }
  );

  // Abre o grupo cujo filho corresponde à rota atual
  useEffect(() => {
    const initial = {};
    filteredRoutes.forEach(({ type, key, name, collapse: children }) => {
      if (type === "collapse" && Array.isArray(children)) {
        const k = key || toKey(name);
        initial[k] = children.some((c) => c.route === location.pathname);
      }
    });
    setOpenCollapse((prev) => ({ ...prev, ...initial }));
  }, [location.pathname, filteredRoutes]);

  return (
    <SidenavRoot
      {...rest}
      variant="permanent"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      ownerState={{
        transparentSidenav,
        whiteSidenav,
        miniSidenav,
        darkMode,
        uiOverrides,
        getGradientFromColor,
      }}
    >
      <MDBox pt={3} pb={1} px={4} textAlign="center">
        <MDBox
          display={{ xs: "block", xl: "none" }}
          position="absolute"
          top={0}
          right={0}
          p={1.625}
          onClick={closeSidenav}
          sx={{ cursor: "pointer" }}
        >
          <MDTypography variant="h6" color="secondary">
            <Icon sx={{ fontWeight: "bold" }}>close</Icon>
          </MDTypography>
        </MDBox>
        <MDBox component={NavLink} to="/" display="flex" alignItems="center">
          {brand && <MDBox component="img" src={brand} alt="Brand" width="2rem" />}
          <MDBox
            width={!brandName && "100%"}
            sx={(theme) => sidenavLogoLabel(theme, { miniSidenav })}
          >
            <MDTypography component="h6" variant="button" fontWeight="medium" color={textColor}>
              {brandName}
            </MDTypography>
          </MDBox>
        </MDBox>
      </MDBox>
      <Divider
        light={
          (!darkMode && !whiteSidenav && !transparentSidenav) ||
          (darkMode && !transparentSidenav && whiteSidenav)
        }
      />
      <List>{renderRoutes}</List>
    </SidenavRoot>
  );
}

Sidenav.defaultProps = { color: "info", brand: "" };

Sidenav.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Sidenav;
